import React from 'react'
import PropTypes from 'prop-types'
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  Stack,
  Center,
  Icon,
  Text,
  Button,
  ButtonGroup,
  useDisclosure,
} from '@chakra-ui/react'

export const Confirmation = React.forwardRef(
  ({ size, colorScheme, icon, title, description, cancelText, confirmText, onConfirm, children, propsOf, ...rest }, ref) => {
    const cancelRef = React.useRef()
    const { isOpen, onOpen: open, onClose: close } = useDisclosure()

    const confirm = React.useCallback(
      (event) => {
        onConfirm && onConfirm(event)
        close()
      },
      [onConfirm, close],
    )

    return (
      <>
        {children({ ref, isOpen, open, close, confirm, ...rest })}

        <AlertDialog
          size={size}
          colorScheme={colorScheme}
          isCentered
          isOpen={isOpen}
          leastDestructiveRef={cancelRef}
          onClose={close}
          {...propsOf.dialog}
        >
          <AlertDialogOverlay {...propsOf.overlay} />
          <AlertDialogContent {...propsOf.content}>
            {(icon || title) && (
              <AlertDialogHeader as={Stack} pt='1.5rem' pb='0.5rem' direction='row' spacing='1rem' alignItems='center' {...propsOf.header}>
                {icon && (
                  <Center boxSize='2rem' bgColor={`${colorScheme}.100`} borderRadius='lg' {...propsOf.iconBox}>
                    <Icon as={icon} boxSize='1.125rem' color={`${colorScheme}.500`} {...propsOf.icon} />
                  </Center>
                )}

                {title && (
                  <Text fontSize='md' {...propsOf.title}>
                    {title}
                  </Text>
                )}
              </AlertDialogHeader>
            )}

            {description && (
              <AlertDialogBody {...propsOf.body}>
                <Text color='gray.700' fontSize='sm' {...propsOf.description}>
                  {description}
                </Text>
              </AlertDialogBody>
            )}

            <AlertDialogFooter as={ButtonGroup} paddingBottom='1.5rem' variant='outline' {...propsOf.footer}>
              <Button ref={cancelRef} onClick={close} {...propsOf.cancel}>
                {cancelText}
              </Button>

              <Button colorScheme={colorScheme} borderColor='inherit' onClick={confirm} {...propsOf.confirm}>
                {confirmText}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    )
  },
)

Confirmation.propTypes = {
  size: PropTypes.string,
  colorScheme: PropTypes.string,
  icon: PropTypes.elementType,
  title: PropTypes.node,
  description: PropTypes.node,
  cancelText: PropTypes.node,
  confirmText: PropTypes.node,
  onConfirm: PropTypes.func,
  children: PropTypes.func,
  propsOf: PropTypes.shape({
    dialog: PropTypes.object,
    overlay: PropTypes.object,
    content: PropTypes.object,
    header: PropTypes.object,
    title: PropTypes.object,
    iconBox: PropTypes.object,
    icon: PropTypes.object,
    body: PropTypes.object,
    description: PropTypes.object,
    footer: PropTypes.object,
    cancel: PropTypes.object,
    confirm: PropTypes.object,
  }),
}

Confirmation.defaultProps = {
  size: 'xs',
  colorScheme: 'red',
  cancelText: 'Cancel',
  confirmText: 'Confirm',
  children: () => null,
  propsOf: {},
}
